import {z} from 'zod'
import {DateRangeFilterEnum, PatientAgeRange} from '@/types'
import {ProjectProgressStatus} from '../types'

export const SiarBulkExportType = z.enum(['open_project', 'close_project'])
export type SiarBulkExportType = z.infer<typeof SiarBulkExportType>

export const SiarExportStatus = z.enum(['pending', 'partially_transmitted', 'transmitted'])
export type SiarExportStatus = z.infer<typeof SiarExportStatus>

export const SiarExportTransmissionStatus = z.enum(['pending', 'transmitted'])
export type SiarExportTransmissionStatus = z.infer<typeof SiarExportTransmissionStatus>

// Progress statuses are split per phase. Opening: to_print → to_export_opening → to_transmit_opening.
// Closing: to_export_closing → to_transmit_closing (no "print" step on closing).
export const SiarOpeningProgressStatus = z.enum(['to_print', 'to_export_opening', 'to_transmit_opening'])
export type SiarOpeningProgressStatus = z.infer<typeof SiarOpeningProgressStatus>

export const SiarClosingProgressStatus = z.enum(['to_export_closing', 'to_transmit_closing'])
export type SiarClosingProgressStatus = z.infer<typeof SiarClosingProgressStatus>

// Union of every export-selectable (i.e. exportable) progress status across both phases.
export const SiarExportProgressStatus = z.enum([
    ...SiarOpeningProgressStatus.options,
    ...SiarClosingProgressStatus.options
])
export type SiarExportProgressStatus = z.infer<typeof SiarExportProgressStatus>

// Generic (phase-agnostic) status vocabulary used on the API wire. The backend receives the generic
// status together with `exportType` and resolves the phase itself (star-be siar_xml_export.service:
// toExportStatus/genericToPhaseStatus). The FE keeps the phase-specific `SiarExportProgressStatus`
// internally and translates at the API boundary (see phaseToGenericStatus/siarCounterForStatus in utils.ts).
export const SiarGenericStatus = z.enum(['to_print', 'to_export', 'to_transmit'])
export type SiarGenericStatus = z.infer<typeof SiarGenericStatus>

// Preview `status` filter (wire format): the generic statuses plus the dedicated "excluded" view
// that returns only non-exportable projects regardless of their progress status.
export const SiarPreviewStatusFilter = z.enum([...SiarGenericStatus.options, 'excluded'])
export type SiarPreviewStatusFilter = z.infer<typeof SiarPreviewStatusFilter>

export const SiarPreviewItemProgressStatus = ProjectProgressStatus.extract([
    'to_print',
    'to_export_opening',
    'to_transmit_opening',
    'to_export_closing',
    'to_transmit_closing'
])
export type SiarPreviewItemProgressStatus = z.infer<typeof SiarPreviewItemProgressStatus>

// Reason a project is not exportable. Extensible: the BE may add new values over time,
// so this is parsed as a plain nullable string and the FE maps known values to labels
// with a generic fallback (see resolveExclusionReasonLabel in utils.ts).
// - missing_initial_evaluation_access → opening export (project without initial evaluation access)
// - opening_not_transmitted → closing export (project whose opening SIAR was not transmitted yet)
export const KNOWN_EXCLUSION_REASONS = ['missing_initial_evaluation_access', 'opening_not_transmitted'] as const
export type KnownExclusionReason = (typeof KNOWN_EXCLUSION_REASONS)[number]

export const ExclusionReason = z.string().nullable()
export type ExclusionReason = z.infer<typeof ExclusionReason>

export const SiarExportCreatedBy = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string()
})
export type SiarExportCreatedBy = z.infer<typeof SiarExportCreatedBy>

export const SiarExport = z.object({
    id: z.string(),
    fileName: z.string(),
    exportType: SiarBulkExportType,
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    status: SiarExportStatus,
    projectCount: z.number(),
    transmittedCount: z.number(),
    dailyProgressive: z.number(),
    addRegistry: z.boolean(),
    createdAt: z.string(),
    createdBy: SiarExportCreatedBy
})
export type SiarExport = z.infer<typeof SiarExport>

export const SiarExportPreviewItemInOtherBatch = z.object({
    id: z.string(),
    fileName: z.string(),
    dailyProgressive: z.number()
})
export type SiarExportPreviewItemInOtherBatch = z.infer<typeof SiarExportPreviewItemInOtherBatch>

export const SiarExportPreviewItem = z.object({
    projectId: z.string(),
    projectCode: z.number(),
    patientFullName: z.string(),
    // Service type (adults / evolutionary age). Optional until the preview BE endpoint returns it,
    // so the current response (without this field) still validates. Consumed by the "Copia elenco PRI" action.
    serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']).nullish(),
    startDate: z.string().nullable(),
    expirationDate: z.string().nullable(),
    endDate: z.string().nullable(),
    progressStatus: SiarPreviewItemProgressStatus,
    transmissionStatus: SiarExportTransmissionStatus,
    transmissionDate: z.string().nullable(),
    openingExportedAt: z.string().nullable(),
    openingTransmissionAt: z.string().nullable(),
    inOtherBatch: SiarExportPreviewItemInOtherBatch.nullable(),
    exclusionReason: ExclusionReason
})
export type SiarExportPreviewItem = z.infer<typeof SiarExportPreviewItem>

// Progress status of a project inside a created export batch. It spans every non-`incomplete`
// progress status because a batched project can sit at any point of its phase flow: still to
// print/export, to transmit, or already transmitted, for whichever phase the batch belongs to.
export const SiarExportProjectProgressStatus = ProjectProgressStatus.extract([
    'to_print',
    'to_export_opening',
    'to_transmit_opening',
    'transmitted_opening',
    'to_export_closing',
    'to_transmit_closing',
    'transmitted_closing'
])
export type SiarExportProjectProgressStatus = z.infer<typeof SiarExportProjectProgressStatus>

export const SiarExportProject = z.object({
    id: z.string(),
    projectId: z.string(),
    projectCode: z.number(),
    patientFullName: z.string(),
    startDate: z.string().nullable(),
    expirationDate: z.string().nullable(),
    endDate: z.string().nullable(),
    progressStatus: SiarExportProjectProgressStatus,
    transmissionStatus: SiarExportTransmissionStatus,
    transmittedAt: z.string().nullable()
})
export type SiarExportProject = z.infer<typeof SiarExportProject>

export const SiarExportDetail = SiarExport.extend({
    projects: z.array(SiarExportProject)
})
export type SiarExportDetail = z.infer<typeof SiarExportDetail>

export const SiarExportHistorySearchParams = z.object({
    exportType: SiarBulkExportType.optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    filters: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    range: DateRangeFilterEnum.optional(),
    projectNumber: z.string().trim().optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().default(20)
})
export type SiarExportHistorySearchParams = z.infer<typeof SiarExportHistorySearchParams>

export const SiarExportsColumn = z.enum([
    'exportType',
    'period',
    'createdAtAndAuthor',
    'status',
    'transmittedAt',
    'name',
    'moreActions'
])
export type SiarExportsColumn = z.infer<typeof SiarExportsColumn>
